import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { colors, fontSizes, fontWeights } from '../theme/theme';

const MessageList = () => {
  const messages = useSelector(state => state.chat.messages);
  const status = useSelector(state => state.chat.status);
  const scrollRef = useRef(null);

  // Sample messages for development
  const sampleMessages = [
    { 
      role: 'kullanıcı', 
      message: 'Bu paragrafı daha resmi bir dille yeniden yazar mısın?',
      timestamp: new Date(Date.now() - 300000).toLocaleTimeString('tr-TR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },
    { 
      role: 'katip', 
      message: 'Elbette, işte daha resmi bir versiyon:\n\n"İşbu sözleşme maddesi uyarınca, taraflar arasında doğan uyuşmazlıkların çözümünde İstanbul Mahkemeleri ve İcra Dairelerinin yetkili olacağı kabul edilmiştir."',
      timestamp: new Date(Date.now() - 240000).toLocaleTimeString('tr-TR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },
    { 
      role: 'kullanıcı', 
      message: 'Seçili metni özetle.',
      timestamp: new Date(Date.now() - 120000).toLocaleTimeString('tr-TR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },
    { 
      role: 'katip', 
      message: 'Seçili metnin özeti:\n\nBu metin, taraflar arasındaki uyuşmazlık çözümünde İstanbul Mahkemelerinin yetkili olacağını belirten bir hukuki düzenleme içermektedir.',
      timestamp: new Date(Date.now() - 60000).toLocaleTimeString('tr-TR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    }
  ];

  const displayMessages = messages.length ? messages : sampleMessages;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [displayMessages]);

  const containerStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    background: colors.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const messageContainerStyle = (isUser) => ({
    display: 'flex',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    alignItems: 'flex-end',
  });

  const messageStyle = (isUser) => ({
    maxWidth: '85%',
    padding: '12px 16px',
    borderRadius: '12px',
    background: isUser ? colors.primary : colors.background,
    color: isUser ? colors.background : colors.textPrimary,
    fontSize: fontSizes.body,
    lineHeight: '1.5',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    position: 'relative',
  });

  const timestampStyle = {
    fontSize: fontSizes.small,
    color: colors.textSecondary,
    marginTop: '4px',
    textAlign: 'right',
  };

  const loadingMessageStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    background: colors.background,
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    maxWidth: '85%',
  };

  const loadingDotsStyle = {
    display: 'flex',
    gap: '4px',
  };

  const dotStyle = {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: colors.textSecondary,
    animation: 'pulse 1.4s ease-in-out infinite both',
  };

  const renderMessage = (msg, index) => {
    const isUser = msg.role === 'kullanıcı';
    
    return (
      <div key={index} style={messageContainerStyle(isUser)}>
        <div style={messageStyle(isUser)}>
          <div>{msg.message}</div>
          {msg.timestamp && (
            <div style={timestampStyle}>
              {msg.timestamp}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={containerStyle} ref={scrollRef}>
      {displayMessages.map((msg, idx) => renderMessage(msg, idx))}
      
      {status === 'loading' && (
        <div style={messageContainerStyle(false)}>
          <div style={loadingMessageStyle}>
            <div style={loadingDotsStyle}>
              <div style={{ ...dotStyle, animationDelay: '0s' }}></div>
              <div style={{ ...dotStyle, animationDelay: '0.2s' }}></div>
              <div style={{ ...dotStyle, animationDelay: '0.4s' }}></div>
            </div>
            <span style={{ fontSize: fontSizes.caption, color: colors.textSecondary, marginLeft: '8px' }}>
              Yanıt yazılıyor...
            </span>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        ::-webkit-scrollbar {
          width: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        
        ::-webkit-scrollbar-thumb {
          background: ${colors.border};
          border-radius: 3px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: ${colors.textSecondary};
        }
      `}</style>
    </div>
  );
};

export default MessageList; 